import { yupResolver } from '@hookform/resolvers/yup';
import {
  Checkbox,
  Dialog, DialogActions, DialogContent,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { GridRowsProp } from '@mui/x-data-grid';
import { FC, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import CustomButton from 'components/CustomButton';
import GenericConfirmationDialog from 'components/GenericConfirmationDialog';
import { ADJUST_GRID_COLUMNS, ADJUST_INVENTORY_DEFAULT_VALUES } from 'containers/Inventory/constants';
import DialogHeader from 'containers/Inventory/DialogHeader';
import ReasonSelector from 'containers/Inventory/ReasonSelector';
import SelectedItemsTable from 'containers/Inventory/SelectedItemsTable';
import { useInventoryContext } from 'context/InventoryContext';

import { putAdjustInventory } from 'services/Inventory';

import { InventoryCustomAction } from 'constants/enums';
import { InventoryCustomActionPayload } from 'types/inventory.interface';
import { handleApiErrors } from 'utils/errorUtils';
import { getOnboardingInfo } from 'utils/storageUtils';
import { AdjustInventoryFormData, getAdjustInventorySchema } from 'validation/inventorySchema';

interface AdjustInventoryDialogProps {
    selectedRows: GridRowsProp;
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const AdjustInventory: FC<AdjustInventoryDialogProps> = ({
  selectedRows,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [adjustedQuantities, setAdjustedQuantities] = useState<Record<string, string>>({});
  const { adjustReasons } = useInventoryContext();
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [enableMetrcSendCheck, setEnableMetrcSendCheck] = useState<boolean>(false);
  const [clientId, setClientId] = useState<string | null>(null);

  const handleAdjustQuantityChange = (e: { itemId: string; value: string }) => {
    setAdjustedQuantities((prev) => ({ ...prev, [e.itemId]: e.value }));
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    getValues,
    setValue,
  } = useForm<AdjustInventoryFormData>({
    defaultValues: ADJUST_INVENTORY_DEFAULT_VALUES,
    resolver: yupResolver(getAdjustInventorySchema(adjustReasons)),
  });

  useEffect(() => {
    const onboardingInfo = getOnboardingInfo();
    setClientId(onboardingInfo?.storeId || null);
    if (onboardingInfo?.store?.isMetrc) {
      setEnableMetrcSendCheck(true);
      setValue('sendToMetrc', true);
    }
  }, []);

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      if (!clientId) {
        throw new Error('Client ID not found');
      }
      const formData = getValues();
      const changedRows = selectedRows.filter((row) => {
        if (!adjustedQuantities[row.itemId]) return false;
        const updated = parseFloat(adjustedQuantities[row.itemId]);
        return updated !== undefined && updated !== Number(row.onHand);
      });

      if (changedRows.length === 0) {
        toast.warning('No changes were made to any of the selected items.');
        return;
      }

      const adjustmentPayload:InventoryCustomActionPayload = {
        type: InventoryCustomAction.ADJUST,
        adjustReasonNote: formData.reasonNote || '',
        adjustReasonId: formData.selectedReason,
        metrcSend: formData.sendToMetrc,
        items: changedRows.map((row) => {
          const updatedQty = parseFloat(adjustedQuantities[row.itemId]);
          return {
            itemId: row.itemId,
            batchId: row.batchId,
            areaId: row.areaId,
            quantity: updatedQty,
            adjustment: updatedQty - parseFloat(row.onHand),
            biotrackAdjustment: updatedQty,
          };
        }),
      };
      await putAdjustInventory(clientId, adjustmentPayload);
      toast.success('Inventory adjusted successfully');
      onSuccess();
    } catch (e) {
      handleApiErrors(e);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDialog = () => {
    setOpenConfirmationDialog(true);
  };

  return (
    <Dialog
      open={isOpen}
      fullWidth
      onClose={onClose}
      aria-describedby="select-adjust-reason"
      slotProps={{
        paper: { sx: { maxWidth: 900, borderRadius: '16px', p: 2.5 } },
      }}
    >
      <DialogHeader title="Adjust Inventory" onClose={onClose} />
      <DialogContent>
        <ReasonSelector
          adjustReasons={adjustReasons}
          control={control}
          errors={errors}
          watch={watch}
        />
        {enableMetrcSendCheck && (
        <Box display="flex" justifyContent="center" mb={2} mt={2}>
          <Typography variant="h6" fontWeight={500} alignSelf="center">
            Send to Metrc
          </Typography>
          <Controller
            name="sendToMetrc"
            control={control}
            render={({ field }) => (
              <Checkbox
                {...field}
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            )}
          />
        </Box>
        )}
        <SelectedItemsTable
          rows={selectedRows}
          columns={ADJUST_GRID_COLUMNS(adjustedQuantities, handleAdjustQuantityChange)}
          customAction={InventoryCustomAction.ADJUST}
        />
      </DialogContent>
      <DialogActions sx={{ p: '0 24px 24px' }}>
        <CustomButton
          id="button-cancelAdjustInventory"
          variant="outlined"
          onClick={onClose}
        >
          Cancel
        </CustomButton>
        <CustomButton
          id="button-confirmAdjustInventory"
          variant="contained"
          onClick={handleSubmit(confirmDialog)}
        >
          Confirm
        </CustomButton>
      </DialogActions>
      {openConfirmationDialog && (
        <GenericConfirmationDialog
          isOpen={openConfirmationDialog}
          dialogMessage="Are you sure you want to adjust the inventory?"
          onClose={() => setOpenConfirmationDialog(false)}
          onSubmit={onSubmit}
          isLoading={isLoading}
        />
      )}
    </Dialog>
  );
};

export default AdjustInventory;
